'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { captureMessage, buildTags } from '@/lib/monitoring/logger';
import { useDeviceClass } from '@/hooks/use-device-class';
import { toast } from 'sonner';

interface InstallPwaButtonProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'icon' | 'default';
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform?: string }>;
}

type NavigatorWithStandalone = Navigator & { standalone?: boolean };

const OFFLINE_ENABLED = process.env.NEXT_PUBLIC_ENABLE_OFFLINE_WORKSPACE === 'true';

export function InstallPwaButton({ className, size = 'sm' }: InstallPwaButtonProps) {
  if (!OFFLINE_ENABLED) {
    return null;
  }
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [installable, setInstallable] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [guidanceOpen, setGuidanceOpen] = useState(false);
  const [guidancePlatform, setGuidancePlatform] = useState<'ios' | 'android' | 'desktop'>('desktop');
  const desktopHintLoggedRef = useRef(false);
  const { isHandheld, platform } = useDeviceClass();
  const isIos = platform === 'ios';

  useEffect(() => {
    const navigatorWithStandalone = window.navigator as NavigatorWithStandalone;
    const isInStandalone = window.matchMedia('(display-mode: standalone)').matches || navigatorWithStandalone.standalone === true;
    setIsStandalone(isInStandalone);

    const handler = (event: Event) => {
      event.preventDefault?.();
      const promptEvent = event as BeforeInstallPromptEvent;
      if (typeof promptEvent.prompt !== 'function') {
        return;
      }
      deferredPromptRef.current = promptEvent;
      setInstallable(true);
      captureMessage('pwa-install.prompt-ready', 'info', {
        tags: buildTags('pwa-install', 'system'),
        extra: { platform }
      });
    };
    window.addEventListener('beforeinstallprompt', handler as EventListener);
    return () => window.removeEventListener('beforeinstallprompt', handler as EventListener);
  }, [platform]);

  useEffect(() => {
    if (!isHandheld && !desktopHintLoggedRef.current) {
      captureMessage('pwa-install.desktop-hint-shown', 'info', {
        tags: buildTags('pwa-install', 'guidance'),
        extra: { platform }
      });
      desktopHintLoggedRef.current = true;
    }
  }, [isHandheld, platform]);

  const guidanceSteps = useMemo(() => {
    if (guidancePlatform === 'ios') {
      return [
        'Tap the Share icon in Safari.',
        'Choose “Add to Home Screen”.',
        'Name the shortcut and confirm. Future lookups will open full screen offline.'
      ];
    }
    if (guidancePlatform === 'android') {
      return [
        'Open the browser menu (⋮).',
        'Tap “Install app” or “Add to Home screen”.',
        'Confirm install so you can open the Offline Workspace like a native app.'
      ];
    }
    return [
      'Use the browser menu to install or pin this app.',
      'On Edge/Chrome you will see an install icon in the address bar.',
      'Once installed, cached data keeps your readiness strip available offline.'
    ];
  }, [guidancePlatform]);

  const openGuidance = (nextPlatform: 'ios' | 'android' | 'desktop') => {
    setGuidancePlatform(nextPlatform);
    setGuidanceOpen(true);
    captureMessage('pwa-install.guidance-opened', 'info', {
      tags: buildTags('pwa-install', 'guidance'),
      extra: { platform: nextPlatform }
    });
  };

  const onInstall = async () => {
    const devicePlatform: 'ios' | 'android' | 'desktop' = isIos ? 'ios' : platform === 'android' ? 'android' : 'desktop';
    captureMessage('pwa-install.clicked', 'info', {
      tags: buildTags('pwa-install', 'action'),
      extra: { installable, platform: devicePlatform }
    });

    if (deferredPromptRef.current) {
      deferredPromptRef.current.prompt();
      const { outcome } = await deferredPromptRef.current.userChoice;
      if (outcome === 'accepted') {
        setInstallable(false);
        deferredPromptRef.current = null;
        captureMessage('pwa-install.accepted', 'info', {
          tags: buildTags('pwa-install', 'action'),
          extra: { platform: devicePlatform }
        });
      } else {
        captureMessage('pwa-install.dismissed', 'warning', {
          tags: buildTags('pwa-install', 'action'),
          extra: { platform: devicePlatform }
        });
      }
      return;
    }

    openGuidance(devicePlatform);
  };

  if (isStandalone) return null;

  const handleDesktopHintClick = () => {
    toast.info('Install Scopax from your phone or tablet via the browser menu → “Add to Home Screen”.');
    openGuidance('desktop');
    captureMessage('pwa-install.desktop-hint-clicked', 'info', {
      tags: buildTags('pwa-install', 'guidance'),
      extra: { platform }
    });
  };

  // Always show the button (gives users a clear affordance). If not yet installable, it shows guidance.
  const buttonSize: 'default' | 'sm' | 'lg' | 'icon' = size === 'md' ? 'default' : (size ?? 'sm');

  const triggerNode = isHandheld ? (
    <Button size={buttonSize} className={className} onClick={onInstall} variant={installable ? 'default' : 'outline'}>
      Install App
    </Button>
  ) : (
    <Button size="sm" variant="ghost" className={className} onClick={handleDesktopHintClick}>
      Install on phone
    </Button>
  );

  return (
    <>
      {triggerNode}

      <Dialog open={guidanceOpen} onOpenChange={setGuidanceOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Install Scopax on this device</DialogTitle>
            <DialogDescription>
              Installing keeps Offline Workspace one tap away so lookups work even without signal.
            </DialogDescription>
          </DialogHeader>
          <ul className="list-decimal space-y-2 pl-5 text-sm text-slate-700">
            {guidanceSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGuidanceOpen(false)}>
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
